import { useCallback, useContext, useEffect, useMemo } from 'react';

import { PlusOutlined, MinusOutlined } from '@ant-design/icons';

import {
  ColSize,
  InputNumberForm,
  RowSize,
  SelectForm,
} from '../../../../../../../../../shared/components';
import { QUOTATION_BUTTON, SectionQuotation } from '../../../../../shared';

import { GenericDataFormContext } from '../../../../../shared/contexts';
import { StepTwoFormContext } from '../../../StepTwo/contexts/StepTwoForm';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm';

import { debounce } from '../../../../../../../../../shared/utils';

import { DECISION_TYPE } from '../../../../../../../../../shared/interfaces';

import { COLORS } from '../../../../../../../../../config/themes/colors';

export const CarPaymentMethods = () => {
  const { paymentMethods, paymentMethodsList } = useContext(
    GenericDataFormContext
  );
  const { stepThreeForm, commertialConditions } = useContext(
    WizardDataFormContext
  );

  const { setLabelButton } = useContext(StepTwoFormContext);

  const maxVariation = useMemo(
    () => commertialConditions?.puntosDeAumentoExtraPrimaVariable!,
    [commertialConditions]
  );

  const minVariation = useMemo(
    () => -commertialConditions?.puntosDeDisminucionExtraPrimaVariable!,
    [commertialConditions]
  );

  const prevValues = useMemo(
    () => ({
      prevEPVValue: Number(commertialConditions?.porcentajeExtraPrimaVariable),
      prevCommissionValue: Number(commertialConditions?.porcentajeDeComision),
    }),
    [commertialConditions]
  );

  const onVariationChange = useCallback(
    async (variation: any) => {
      setLabelButton(QUOTATION_BUTTON.RECOTIZAR);

      if (variation?.toString().length) {
        const prevCommissionValue = Number(prevValues.prevCommissionValue);
        const prevEPVValue = Number(prevValues.prevEPVValue);

        if (prevCommissionValue !== undefined && prevEPVValue !== undefined) {
          stepThreeForm?.setFieldValue(
            `extraVariable`,
            prevEPVValue + variation
          );

          stepThreeForm?.setFieldValue(
            `commission`,
            prevCommissionValue + variation
          );
        }
      }
    },
    [setLabelButton, prevValues, stepThreeForm]
  );

  const setDefaultPaymentMethod = useCallback(() => {
    if (!stepThreeForm?.getFieldValue(`paymentMethod`)) {
      const defaultPaymentMethod = paymentMethodsList.find(
        (el) => el.esDefault === DECISION_TYPE.YES
      );
      if (defaultPaymentMethod) {
        stepThreeForm?.setFieldValue(
          `paymentMethod`,
          `${defaultPaymentMethod.codigoFormaDePago}-${defaultPaymentMethod.nroPlanDePago}`
        );
        stepThreeForm?.setFieldValue(`paymentInstallments`,`${defaultPaymentMethod.cantCuotas}`
        );
      } else {
        const firstPaymentMethod = paymentMethodsList[0];
        stepThreeForm?.setFieldValue(
          `paymentMethod`,
          `${firstPaymentMethod.codigoFormaDePago}-${firstPaymentMethod.nroPlanDePago}`
        );
        
      }
    }
  }, [stepThreeForm, paymentMethodsList]);

  const onChangePaymentMethod = useCallback(async () => {
    try {
      setLabelButton(QUOTATION_BUTTON.RECOTIZAR);
    } catch (error) {}
  }, [setLabelButton]);

  const setCommissionAndEpv = useCallback(() => {
    if (!stepThreeForm.getFieldValue('commission')) {
      stepThreeForm.setFieldValue('commission', prevValues.prevCommissionValue);
    }

    if (!stepThreeForm.getFieldValue('extraVariable')) {
      stepThreeForm.setFieldValue('extraVariable', prevValues.prevEPVValue);
    }
  }, [stepThreeForm, prevValues]);

  const setInitialValues = useCallback(() => {
    setDefaultPaymentMethod();
    setCommissionAndEpv();
  }, [setDefaultPaymentMethod, setCommissionAndEpv]);

  useEffect(() => {
    setInitialValues();
  }, [setInitialValues]);

  return (
    <SectionQuotation level='h3' title='Forma de pago y comisiones'>
      <RowSize className='w-100'>
        <ColSize size={20}>
          <SelectForm
            formProps={{ label: 'Forma de pago:', name: `paymentMethod` }}
            selectProps={{
              options: paymentMethods,
              style: { backgroundColor: COLORS.neutrals[0] },
              onChange: onChangePaymentMethod,
            }}
          />
        </ColSize>

        <ColSize size={20}>
          <InputNumberForm
            formProps={{
              label: 'Comisión:',
              name: `commission`,
            }}
            inputNumberProps={{
              disabled: true,
              precision: 2,
              readOnly: true,
            }}
          />
        </ColSize>

        <ColSize size={20}>
          <InputNumberForm
            formProps={{
              label: 'Extra prima variable:',
              name: `extraVariable`,
            }}
            inputNumberProps={{
              disabled: true,
              precision: 2,
              readOnly: true,
            }}
          />
        </ColSize>

        <ColSize size={20}>
          <InputNumberForm
            formProps={{
              initialValue: 0,
              label: 'Variación:',
              name: `variation`,
            }}
            inputNumberProps={{
              controls: {
                upIcon: <PlusOutlined />,
                downIcon: <MinusOutlined />,
              },
              addonAfter: '%',
              max: maxVariation,
              min: minVariation,
              onChange: debounce((variation) => onVariationChange(variation)),
            }}
          />
        </ColSize>
      </RowSize>
    </SectionQuotation>
  );
};
